import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import MoreTabMenu, { pressMoreTabMenu } from 'components/navigation/MoreTabMenu';
import { Tooltip } from 'components/ui/tooltip';
import isEqual from 'lodash/isEqual';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'store';
import { triggerNotFound } from 'store/reducers/notFound';
import { LocationState } from 'types/tabState';
import { cn } from 'utils/cn';
import { createTabState } from 'utils/tabStateUtil';

// Monotonic counter — notFound holds a single triggerKey compared by value, so re-dispatching
// the same `tab:<name>` would not re-fire the effects watching it. This keeps repeats distinct.
let tabNotFoundCounter = 0;

const MORE_TAB_ID = 'more';

export type TabType = {
    id: string;
    label: string | React.ReactNode;
    displayLabel?: string | React.ReactNode;
    component?: React.ReactNode;
    childComponents?: React.ReactNode[];
    childTabIds?: string[];
    /** Classes for this tab's panel, after the gallery's `panelClassName`. */
    className?: string;
    icon?: string | React.ReactElement | undefined;
    isDropdown?: boolean;
    hidden?: boolean;
    tooltip?: string;
};

interface TabsGalleryProps {
    tabs: TabType[];
    /** Classes for the tab strip's row. */
    className?: string;
    /** Tailwind classes on the outer wrapper and on the active tab's panel. A page that bounds the
     *  gallery's height (flex column, `min-h-0`, panel `overflow-y-auto`) so a list inside the panel
     *  scrolls on its own uses these. */
    wrapperClassName?: string;
    panelClassName?: string;
    initialActiveTab?: number;
    tabGroupId?: string;
    trackTabsInHistoryState?: boolean;
    usePathBasedRouting?: boolean;
    onTabChange?: (tabId: string) => void;
    /** Tabs gathered, in this order, under one More menu tab at the end of the strip. Hidden ones are
     *  left out, and with none left there is no More. Its own id is not a URL: it goes to 404. */
    moreTabs?: TabType[];
    /** Route tab ids that name no tab here, each opening the first visible tab of its list instead. */
    tabAliases?: Record<string, string[]>;
    /** Scroll the strip behind chevrons instead of a scrollbar, as `LineTabsList` does. */
    arrows?: { back: string; next: string };
    /**
     * Opt-in: the active panel becomes a tab panel its tab names (`role="tabpanel"`, `aria-labelledby`, and the
     * tab's `aria-controls`), placed by this function. What the function draws beside it, such as a side column,
     * stays outside the panel and stays mounted while the tabs switch.
     */
    panelFrame?: (panel: React.ReactNode) => React.ReactNode;
}

export interface TabsGalleryRef {
    updateActiveTab: (tabNumber?: number) => void;
    updateChildComponentIndex: (tabId: string, childIndex: number) => void;
}

function TabPanel({
    children,
    className,
    panelRef,
    ...aria
}: {
    children: React.ReactNode | React.ReactNode[];
    className?: string;
    panelRef?: React.Ref<HTMLDivElement>;
    id?: string;
    role?: 'tabpanel';
    'aria-labelledby'?: string;
}) {
    return children ? (
        <div ref={panelRef} className={className} {...aria}>
            {children}
        </div>
    ) : null;
}

const TabsGallery: React.ForwardRefRenderFunction<TabsGalleryRef, TabsGalleryProps> = (
    {
        tabs,
        className,
        wrapperClassName,
        panelClassName,
        initialActiveTab = 0,
        tabGroupId,
        trackTabsInHistoryState,
        usePathBasedRouting = false,
        onTabChange,
        moreTabs,
        tabAliases,
        arrows,
        panelFrame,
    },
    ref
) => {
    // Only with `panelFrame`: the other strips' tabs and panels keep the markup they had.
    const ariaPrefix = useId();
    const isTrackingTabState = tabGroupId !== undefined && trackTabsInHistoryState;
    const location = useLocation();
    const { hash, pathname, search } = location;
    const state = location.state as LocationState;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const moreChildren = useMemo(() => moreTabs?.filter((tab) => !tab.hidden) ?? [], [moreTabs]);
    const moreTab = useMemo(
        (): TabType | undefined =>
            moreChildren.length > 0
                ? {
                      id: MORE_TAB_ID,
                      label: null,
                      isDropdown: true,
                      childTabIds: moreChildren.map((tab) => tab.id),
                      childComponents: moreChildren.map((tab) => tab.component),
                  }
                : undefined,
        [moreChildren]
    );
    const visibleTabs = useMemo(() => {
        const shown = tabs.filter((tab) => !tab.hidden);
        return moreTab ? [...shown, moreTab] : shown;
    }, [tabs, moreTab]);

    // O(1) lookup of child-tab id → owning parent tab + index, instead of nested .indexOf in a loop.
    const childTabLookup = useMemo(() => {
        const map = new Map<string, { tabId: string; childIndex: number }>();
        for (const tab of visibleTabs) {
            if (tab.childTabIds) {
                tab.childTabIds.forEach((childId, idx) => {
                    map.set(childId, { tabId: tab.id, childIndex: idx });
                });
            }
        }
        return map;
    }, [visibleTabs]);

    const getTabFromPath = useCallback((): { tabId: string | null; childTabId?: string; childIndex?: number } => {
        if (!usePathBasedRouting) return { tabId: null };
        const pathSegments = pathname.split('/').filter(Boolean);

        // Walk back from the end to find the first segment matching a known tab id or childTabId.
        // This lets nested routes like /contact/:id/invoices/:invoiceTab still resolve to the
        // 'invoices' main tab even though the actual URL ends with the sub-tab key.
        for (let i = pathSegments.length - 1; i >= 0; i--) {
            const segment = pathSegments[i];
            if (!segment || segment.match(/^\d+$/)) continue;

            const childMatch = childTabLookup.get(segment);
            if (childMatch) {
                return { tabId: childMatch.tabId, childTabId: segment, childIndex: childMatch.childIndex };
            }
            if (visibleTabs.some((tab) => tab.id === segment)) {
                return { tabId: segment };
            }
        }

        return { tabId: null };
    }, [usePathBasedRouting, pathname, childTabLookup, visibleTabs]);

    // Keep the URL in sync with the active tab when path-based routing is on:
    //   - If the matched route's `:tab` param is set but doesn't resolve to a visible
    //     main or child tab, follow its `tabAliases` entry, else redirect to /404 (instead of
    //     silently rendering the first tab).
    //   - If the URL has no tab segment at all (the page's base path), redirect to
    //     `${basePath}/${firstTabId}` so the active tab is reflected in the URL.
    // Pages whose tabs are registered as their own static routes (e.g. /settings) have no
    // `:tab` param; the second branch still moves the user from /settings to
    // /settings/<firstTab>.
    const { tab: routeTab } = useParams();
    useEffect(() => {
        if (!usePathBasedRouting || visibleTabs.length === 0) return;

        if (routeTab) {
            const isMainTab = visibleTabs.some((tab) => tab !== moreTab && tab.id === routeTab);
            const isChildTab = childTabLookup.has(routeTab);
            if (!isMainTab && !isChildTab) {
                const aliasTarget = tabAliases?.[routeTab]?.find(
                    (id) => childTabLookup.has(id) || visibleTabs.some((tab) => tab.id === id)
                );
                if (aliasTarget) {
                    const segments = pathname.split('/');
                    segments[segments.lastIndexOf(routeTab)] = aliasTarget;
                    navigate(segments.join('/'), { replace: true });
                } else {
                    dispatch(triggerNotFound(`tab:${routeTab}:${++tabNotFoundCounter}`));
                }
            }
            return;
        }

        // Resolve the active tab from the whole path, not just the last segment: nested sub-tab
        // routes like /contact/:id/invoices/:invoiceTab end in an invoice sub-tab key (e.g. `cr`)
        // that isn't a TabsGallery tab id. Walking back via getTabFromPath still finds the owning
        // 'invoices' tab, so we don't mistake the URL for a tab-less base path and append a default
        // tab segment, which would leave an unmatched route and redirect to /404.
        const { tabId } = getTabFromPath();
        if (tabId) return;

        const basePath = pathname.replace(/\/$/, '');
        const targetIndex = Math.max(0, Math.min(initialActiveTab, visibleTabs.length - 1));
        navigate(`${basePath}/${visibleTabs[targetIndex].id}`, { replace: true });
    }, [
        usePathBasedRouting,
        routeTab,
        visibleTabs,
        childTabLookup,
        pathname,
        navigate,
        dispatch,
        initialActiveTab,
        getTabFromPath,
        moreTab,
        tabAliases,
    ]);

    const getBasePathWithoutTab = useCallback(() => {
        if (!usePathBasedRouting) return pathname;
        const pathSegments = pathname.split('/').filter(Boolean);

        for (let i = pathSegments.length - 1; i >= 0; i--) {
            const segment = pathSegments[i];
            if (!segment || segment.match(/^\d+$/)) continue;

            const isMainTab = visibleTabs.some((tab) => tab.id === segment);
            const isChildTab = visibleTabs.some((tab) => tab.childTabIds?.includes(segment));
            if (isMainTab || isChildTab) {
                return '/' + pathSegments.slice(0, i).join('/');
            }
        }
        return pathname;
    }, [usePathBasedRouting, pathname, visibleTabs]);

    const visibleTabsCurrentIndex = useMemo(() => {
        if (usePathBasedRouting) {
            const { tabId } = getTabFromPath();
            if (tabId) {
                const index = visibleTabs.findIndex((tab) => tab.id === tabId);
                return index !== -1 ? index : initialActiveTab;
            }
            return initialActiveTab;
        }

        if (tabGroupId) {
            const targetTabId = searchParams.get('tab') ?? state?.tabs?.[tabGroupId]?.main;
            return visibleTabs.findIndex((tab) => tab.id === targetTabId);
        }

        return initialActiveTab;
    }, [usePathBasedRouting, getTabFromPath, visibleTabs, initialActiveTab, tabGroupId, searchParams, state]);

    const [currentTab, setCurrentTab] = useState<number>(
        usePathBasedRouting || (tabGroupId && visibleTabsCurrentIndex !== undefined && visibleTabsCurrentIndex > -1)
            ? visibleTabsCurrentIndex
            : initialActiveTab
    );
    const currentTabObject = visibleTabs.at(currentTab);

    const currentSubTabIndexFromState =
        tabGroupId && state?.tabs?.[tabGroupId]?.sub !== undefined ? Number(state?.tabs?.[tabGroupId]?.sub) : undefined;
    const currentSubTabIndexFromUrl =
        tabGroupId && searchParams.get('subtab') !== null ? Number(searchParams.get('subtab')) : undefined;

    const initialSubTabIndexFromPath = usePathBasedRouting ? getTabFromPath().childIndex : undefined;
    const initialSubTabIndex = initialSubTabIndexFromPath ?? currentSubTabIndexFromUrl ?? currentSubTabIndexFromState;
    const initialEntries: [string, number][] =
        initialSubTabIndex !== undefined && currentTabObject?.id ? [[currentTabObject.id, initialSubTabIndex]] : [];

    // subtab state
    const [currentChildComponentIndexByTabId, setCurrentChildComponentIndexByTabId] = useState<Map<string, number>>(
        new Map(initialEntries)
    );

    const makeState = useCallback(
        (tabIndex: number, subTabIndex?: number): LocationState => {
            const initialVisibleTab = visibleTabs.at(tabIndex);
            if (tabGroupId === undefined || initialVisibleTab === undefined) {
                return { ...state };
            }

            return {
                ...state,
                tabs: {
                    ...state?.tabs,
                    ...createTabState(tabGroupId, initialVisibleTab.id, subTabIndex).tabs,
                },
            };
        },
        [state, tabGroupId, visibleTabs]
    );

    // Sync local state with pathname changes for path-based routing, AND with location.state on
    // history change. Render-time prev-value compare against external inputs
    // (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    const [prevPathname, setPrevPathname] = useState(pathname);
    const [prevLocationState, setPrevLocationState] = useState(state);
    const [prevVisibleTabs, setPrevVisibleTabs] = useState(visibleTabs);
    if (pathname !== prevPathname || state !== prevLocationState || visibleTabs !== prevVisibleTabs) {
        setPrevPathname(pathname);
        setPrevLocationState(state);
        setPrevVisibleTabs(visibleTabs);

        if (usePathBasedRouting) {
            const { tabId, childIndex } = getTabFromPath();
            const targetIndex = tabId ? visibleTabs.findIndex((tab) => tab.id === tabId) : 0;

            if (targetIndex !== -1 && targetIndex !== currentTab) {
                setCurrentTab(targetIndex);
            }

            if (tabId) {
                setCurrentChildComponentIndexByTabId((prev) => {
                    const currentValue = prev.get(tabId);
                    if (childIndex !== undefined) {
                        if (currentValue === childIndex) return prev;
                        const newMap = new Map(prev);
                        newMap.set(tabId, childIndex);
                        return newMap;
                    }
                    // URL points back to the parent tab — drop the stale child selection so the
                    // next visit doesn't restore the previously selected sub-tab.
                    if (currentValue === undefined) return prev;
                    const newMap = new Map(prev);
                    newMap.delete(tabId);
                    return newMap;
                });
            }
        } else if (isTrackingTabState) {
            const tab = visibleTabs.find((tab) => tab.id === state?.tabs?.[tabGroupId]?.main);
            const tabIndex = visibleTabs.findIndex((tab) => tab.id === state?.tabs?.[tabGroupId]?.main);

            if (typeof tabIndex === 'number' && tabIndex > -1 && tab !== undefined) {
                if (tabIndex !== currentTab) {
                    setCurrentTab(tabIndex);
                }
                if (currentSubTabIndexFromState !== undefined) {
                    const subTabIndex = currentChildComponentIndexByTabId.get(tab.id);
                    if (subTabIndex !== currentSubTabIndexFromState) {
                        setCurrentChildComponentIndexByTabId(new Map([[tab.id, currentSubTabIndexFromState]]));
                    }
                }
            }
        }
    }

    // On mount, write the initial tab state if missing
    useEffect(() => {
        if (!isTrackingTabState || usePathBasedRouting) {
            return;
        }
        if (state?.tabs?.[tabGroupId] === undefined) {
            const tabIdFromUrl = searchParams.get('tab');
            const subTabIndexFromUrl = searchParams.get('subtab');
            const tabFromUrlIndex = visibleTabs.findIndex((tab) => tab.id === tabIdFromUrl);
            const newState = makeState(
                tabFromUrlIndex > -1 ? tabFromUrlIndex : initialActiveTab,
                subTabIndexFromUrl !== null ? Number(subTabIndexFromUrl) : undefined
            );
            searchParams.delete('tab');
            searchParams.delete('subtab');
            navigate(`${pathname}?${searchParams}${hash}`, {
                replace: true,
                state: newState,
            });
        }
    }, [
        state,
        navigate,
        tabGroupId,
        initialActiveTab,
        pathname,
        search,
        hash,
        isTrackingTabState,
        visibleTabs,
        makeState,
        searchParams,
        usePathBasedRouting,
    ]);

    const updateChildComponentIndex = useCallback(
        (tabId: string, childIndex: number, skipStateUpdate = false) => {
            const newMapState = new Map();
            newMapState.set(tabId, childIndex);
            setCurrentChildComponentIndexByTabId(newMapState);

            const index = visibleTabs.findIndex((tab) => tab.id === tabId);
            if (index >= 0) {
                setCurrentTab(index);
            }

            if (usePathBasedRouting && !skipStateUpdate) {
                const targetTab = visibleTabs.find((tab) => tab.id === tabId);
                if (targetTab?.childTabIds && targetTab.childTabIds[childIndex]) {
                    const basePath = getBasePathWithoutTab();
                    const childTabId = targetTab.childTabIds[childIndex];
                    navigate(`${basePath}/${childTabId}${hash}`, {
                        replace: false,
                    });
                }
                return;
            }

            if (!skipStateUpdate && isTrackingTabState) {
                const newState = makeState(index, childIndex);
                const combinedNewState = { ...state, ...newState };
                if (isEqual(state, combinedNewState)) {
                    return;
                }
                navigate(`${pathname}${searchParams.size > 0 ? `?${searchParams}` : ''}${hash}`, {
                    state: combinedNewState,
                    replace: false,
                });
            }
        },
        [
            visibleTabs,
            navigate,
            state,
            pathname,
            searchParams,
            hash,
            makeState,
            isTrackingTabState,
            usePathBasedRouting,
            getBasePathWithoutTab,
        ]
    );

    const updateActiveTab = (tabNumber?: number) => {
        if (tabNumber != null && tabNumber >= 0 && visibleTabs.length > tabNumber && visibleTabs[tabNumber].component) {
            if (usePathBasedRouting) {
                const basePath = getBasePathWithoutTab();
                const targetTab = visibleTabs[tabNumber];
                const newPath = `${basePath}/${targetTab.id}`;
                navigate(`${newPath}${hash}`, {
                    replace: false,
                });
                return;
            }

            setCurrentTab(tabNumber);
            if (!isTrackingTabState) {
                return;
            }
            const newState = makeState(tabNumber);
            navigate(`${pathname}${search}${hash}`, {
                replace: false,
                state: newState,
            });
        }
    };

    useImperativeHandle(ref, () => ({
        updateActiveTab,
        updateChildComponentIndex,
    }));

    const activeChildIndex = currentTabObject?.id
        ? (currentChildComponentIndexByTabId.get(currentTabObject.id) ?? -1)
        : -1;

    // A newly shown panel is often empty for a frame or two before its content mounts, and on a page
    // that grows with its content the footer below it would jump up and back. The gallery keeps the
    // height it last had until the new panel has a box of its own; a panel that stays empty keeps the
    // hold until the next switch.
    const rootRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const moreTabRef = useRef<HTMLButtonElement>(null);
    const [moreOpen, setMoreOpen] = useState<false | 'pointer' | 'keys'>(false);
    const lastHeightRef = useRef(0);
    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        const observer = new ResizeObserver(() => {
            lastHeightRef.current = root.offsetHeight;
        });
        observer.observe(root);
        return () => observer.disconnect();
    }, []);
    const activePanelKey = `${currentTabObject?.id}:${activeChildIndex}`;
    useLayoutEffect(() => {
        const root = rootRef.current;
        const panel = panelRef.current;
        const heldHeight = lastHeightRef.current;
        if (!root || !panel || panel.offsetHeight > 0 || heldHeight === 0) return undefined;
        root.style.minHeight = `${heldHeight}px`;
        const release = new ResizeObserver(() => {
            if (panel.offsetHeight > 0) {
                root.style.minHeight = '';
                release.disconnect();
            }
        });
        release.observe(panel);
        return () => {
            release.disconnect();
            root.style.minHeight = '';
        };
    }, [activePanelKey]);

    // Where the strip is stuck over a page that scrolls as one, the page keeps the offset the last panel was
    // scrolled to: a panel shown under the strip is brought back to its top, clear of the strip by the page's
    // scroll padding.
    useLayoutEffect(() => {
        const strip = rootRef.current?.firstElementChild;
        const panel = panelRef.current;
        if (!strip || !panel || getComputedStyle(strip).position !== 'sticky') return;
        if (panel.getBoundingClientRect().top < strip.getBoundingClientRect().bottom - 1) panel.scrollIntoView();
    }, [activePanelKey]);

    const getTablePanel = useCallback(
        (component: React.ReactNode, _index: number, tab: TabType) => {
            return (
                <TabPanel
                    key={`tab-panel-${tab.id}`}
                    panelRef={panelRef}
                    className={cn(panelClassName, tab.className)}
                    {...(panelFrame && {
                        id: `${ariaPrefix}-panel-${tab.id}`,
                        role: 'tabpanel' as const,
                        'aria-labelledby': `${ariaPrefix}-tab-${tab.id}`,
                    })}
                >
                    {component ? component : (tab.displayLabel ?? tab.label)}
                </TabPanel>
            );
        },
        [panelClassName, panelFrame, ariaPrefix]
    );
    const panels = visibleTabs.map((tab, index) => {
        const { component: Component, childComponents } = tab;
        if (index === currentTab) {
            if (childComponents != null && activeChildIndex >= 0 && childComponents.length > activeChildIndex) {
                const childComponent = childComponents[activeChildIndex];
                return getTablePanel(childComponent, index, tab);
            } else {
                return getTablePanel(Component, index, tab);
            }
        }
        return null;
    });

    return (
        <LineTabs
            value={currentTab}
            onValueChange={(value: number) => {
                if (visibleTabs[value]?.component) {
                    updateActiveTab(value);
                    onTabChange?.(visibleTabs[value].id);
                }
            }}
            className={wrapperClassName}
            data-tab-group={tabGroupId}
            ref={rootRef}
        >
            <LineTabsList arrows={arrows} className={cn('mb-4', className)}>
                {visibleTabs.map((tab, index) => {
                    const trigger = (
                        <LineTab
                            key={`tab-${tab.id}`}
                            value={index}
                            {...(panelFrame && {
                                id: `${ariaPrefix}-tab-${tab.id}`,
                                'aria-controls': index === currentTab ? `${ariaPrefix}-panel-${tab.id}` : undefined,
                            })}
                            icon={tab.icon}
                            reserveSelectedWidth={!tab.isDropdown}
                            // The menu's trigger carries the padding, so the whole tab opens it; `new:` beats the
                            // new design's own tab padding.
                            // While More's menu is open from the keys, focus is on a row of the menu: the tab keeps
                            // the kit tab's focus style, as the one the menu belongs to.
                            className={cn(
                                tab.isDropdown && 'new:p-0 p-0',
                                tab === moreTab &&
                                    'new:data-menu-keys:border-ring new:data-menu-keys:ring-ring/50 new:data-menu-keys:outline-ring new:data-menu-keys:ring-[3px] new:data-menu-keys:outline-1'
                            )}
                            // More is a menu button as well as a tab: the arrow keys reach it as any tab, and
                            // on it Enter, Space and ↓ open its menu.
                            {...(tab === moreTab && {
                                ref: moreTabRef,
                                'aria-haspopup': 'menu' as const,
                                'aria-expanded': !!moreOpen,
                                'data-menu-keys': moreOpen === 'keys' ? '' : undefined,
                                onKeyDown: pressMoreTabMenu,
                                onKeyUp: pressMoreTabMenu,
                            })}
                        >
                            {tab === moreTab ? (
                                <MoreTabMenu
                                    tabs={moreChildren}
                                    activeIndex={index === currentTab ? activeChildIndex : -1}
                                    onSelect={(childIndex) => updateChildComponentIndex(MORE_TAB_ID, childIndex)}
                                    onOpenChange={(open, byKeys) => setMoreOpen(open && (byKeys ? 'keys' : 'pointer'))}
                                    tabRef={moreTabRef}
                                />
                            ) : (
                                tab.label
                            )}
                        </LineTab>
                    );
                    return tab.tooltip ? (
                        <Tooltip key={`tab-${tab.id}`} content={tab.tooltip}>
                            {trigger}
                        </Tooltip>
                    ) : (
                        trigger
                    );
                })}
            </LineTabsList>
            {panelFrame ? panelFrame(panels) : panels}
        </LineTabs>
    );
};

export default forwardRef(TabsGallery);
